<script setup lang="ts">
import { ButtonType } from '../types/common';
import PageHeader from './PageHeader.vue';

withDefaults(defineProps<{
    title?: string;
    description?: string;
    labels?: string[];
    loading?: boolean;
}>(), {
    title: '',
    description: '',
    labels: () => [],
    loading: false
});

defineEmits<{
    back: [];
    edit: [event: Event];
}>();

const slots = useSlots();
const infoPopover = useTemplateRef<{ show:(event: Event) => void; hide: (event: Event) => void }>('infoPopover');
</script>

<template>
    <page-header>
        <div class="flex items-center gap-1 min-w-0">
            <VButton
                icon="pi pi-chevron-left"
                :type="ButtonType.LINK_PRIMARY"
                @click="$emit('back')"
            />
            <div class="self-center w-px h-10 bg-[var(--main-border-color-dark)] mr-[10px]" />

            <VLPopover ref="infoPopover" class="max-w-[360px]">
                <div class="flex flex-col gap-2">
                    <span class="text-base font-normal text-[var(--primary-color)]">{{ title }}</span>
                    <span v-if="description" class="text-sm font-light text-[var(--primary-color)] break-words whitespace-normal">
                        {{ description }}
                    </span>
                    <div v-if="labels.length" class="flex flex-wrap gap-1">
                        <VLTag
                            v-for="label in labels"
                            :key="label"
                            class="!text-xs !text-[var(--published-color)] !bg-[var(--secondary-color-selected)]"
                        >
                            {{ label }}
                        </VLTag>
                    </div>
                </div>
            </VLPopover>

            <div
                class="flex flex-col justify-center max-w-[360px] min-w-0"
                @mouseenter="(event) => !loading && infoPopover!.show(event)"
                @mouseleave="(event) => !loading && infoPopover!.hide(event)"
            >
                <VLSkeleton v-if="loading" width="280px" height="22px" />
                <div v-else class="flex items-center min-w-0">
                    <slot name="status" />
                    <span class="text-lg text-[var(--primary-color)] truncate font-normal leading-[1.2rem] pt-1 cursor-pointer">
                        {{ title }}
                    </span>
                    <VButton
                        icon="pi pi-pencil"
                        :type="ButtonType.LINK_PRIMARY"
                        class="shrink-0 !w-8 !h-5 !p-0 !min-w-0 ml-[8px]"
                        @click="$emit('edit', $event)"
                    />
                </div>

                <VLSkeleton v-if="loading" width="280px" height="16px" />
                <div v-else-if="description" class="flex items-center w-full h-[20px]">
                    <span class="text-sm truncate flex-1 min-w-0 font-light text-[var(--primary-color)] leading-[1.2rem] cursor-pointer">
                        {{ description }}
                    </span>
                </div>
            </div>

            <template v-if="!loading && slots.afterInfo">
                <div class="self-center w-px h-10 bg-[var(--main-border-color-dark)] mr-[10px] ml-[8px]" />
                <slot name="afterInfo" />
            </template>
        </div>

        <template v-if="slots.actions" #actions>
            <slot name="actions" />
        </template>
    </page-header>
</template>
